import dayjs from "dayjs";

const roundCurrency = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const buildAmortizationSchedule = ({
  principal,
  annualRate,
  termMonths,
  startDate,
  paymentType = "fijo",
}) => {
  const monto = Number(principal) || 0;
  const tasaAnual = Number(annualRate) || 0;
  const plazo = Number(termMonths) || 0;

  if (monto <= 0 || plazo <= 0) {
    return [];
  }

  const tasaMensual = tasaAnual / 12 / 100;
  const fechaBase = dayjs(startDate);
  const pagoBaseFijo =
    tasaMensual === 0
      ? monto / plazo
      : (monto * tasaMensual) / (1 - Math.pow(1 + tasaMensual, -plazo));
  const amortizacionCapitalConstante = monto / plazo;

  let saldo = roundCurrency(monto);

  return Array.from({ length: plazo }, (_, index) => {
    const interes = roundCurrency(saldo * tasaMensual);
    let capital =
      paymentType === "decreciente"
        ? roundCurrency(amortizacionCapitalConstante)
        : roundCurrency(pagoBaseFijo - interes);
    let pago =
      paymentType === "decreciente"
        ? roundCurrency(capital + interes)
        : roundCurrency(pagoBaseFijo);

    if (index === plazo - 1) {
      capital = roundCurrency(saldo);
      pago = roundCurrency(capital + interes);
    }

    const saldoInicial = roundCurrency(saldo);
    const saldoFinal = roundCurrency(Math.max(0, saldoInicial - capital));
    saldo = saldoFinal;

    return {
      numero: index + 1,
      fechaProgramada: fechaBase.add(index + 1, "month").format("YYYY-MM-DD"),
      pagoProgramado: pago,
      capitalProgramado: capital,
      interesProgramado: interes,
      saldoInicial,
      saldoFinal,
    };
  });
};

export const applyPaymentsToSchedule = (schedule = [], payments = [], principal = 0) => {
  const scheduleState = schedule.map((item) => ({
    ...item,
    pagadoCapital: 0,
    pagadoInteres: 0,
  }));

  const sortedPayments = [...payments].sort((a, b) => {
    const fechaA = new Date(a.fecha || a.createdAt || 0).getTime();
    const fechaB = new Date(b.fecha || b.createdAt || 0).getTime();
    return fechaA - fechaB;
  });

  const paymentsApplied = sortedPayments.map((payment) => {
    let restante = roundCurrency(payment.monto);
    let interesAplicado = 0;
    let capitalAplicado = 0;
    const fechaPago = new Date(payment.fecha || payment.createdAt || 0).getTime();

    for (const item of scheduleState) {
      if (restante <= 0) {
        break;
      }

      const cuotaVencida = new Date(item.fechaProgramada).getTime() <= fechaPago;

      if (cuotaVencida) {
        const pendienteInteres = roundCurrency(item.interesProgramado - item.pagadoInteres);
        if (pendienteInteres > 0) {
          const abonoInteres = roundCurrency(Math.min(restante, pendienteInteres));
          item.pagadoInteres = roundCurrency(item.pagadoInteres + abonoInteres);
          interesAplicado = roundCurrency(interesAplicado + abonoInteres);
          restante = roundCurrency(restante - abonoInteres);
        }
      }

      const pendienteCapital = roundCurrency(item.capitalProgramado - item.pagadoCapital);
      if (restante > 0 && pendienteCapital > 0) {
        const abonoCapital = roundCurrency(Math.min(restante, pendienteCapital));
        item.pagadoCapital = roundCurrency(item.pagadoCapital + abonoCapital);
        capitalAplicado = roundCurrency(capitalAplicado + abonoCapital);
        restante = roundCurrency(restante - abonoCapital);
      }
    }

    return {
      ...payment,
      interesAplicado,
      capitalAplicado,
      excedente: restante > 0 ? restante : 0,
    };
  });

  let capitalPagadoAcumulado = 0;
  const scheduleApplied = scheduleState.map((item) => {
    capitalPagadoAcumulado = roundCurrency(capitalPagadoAcumulado + item.pagadoCapital);
    const totalPagado = roundCurrency(item.pagadoCapital + item.pagadoInteres);
    const pendiente = roundCurrency(item.pagoProgramado - totalPagado);
    const saldoVivo = roundCurrency(Math.max(0, Number(principal) - capitalPagadoAcumulado));

    return {
      ...item,
      totalPagado,
      pendiente,
      saldoVivo,
      estado:
        pendiente <= 0
          ? "Pagado"
          : totalPagado > 0
          ? "Parcial"
          : "Pendiente",
    };
  });

  const totalCapitalPagado = roundCurrency(
    scheduleApplied.reduce((acc, item) => acc + item.pagadoCapital, 0)
  );
  const totalInteresPagado = roundCurrency(
    scheduleApplied.reduce((acc, item) => acc + item.pagadoInteres, 0)
  );

  return {
    scheduleApplied,
    paymentsApplied,
    totals: {
      totalCapitalPagado,
      totalInteresPagado,
      totalPagado: roundCurrency(totalCapitalPagado + totalInteresPagado),
      saldoActual: roundCurrency(Math.max(0, Number(principal) - totalCapitalPagado)),
    },
  };
};

export const buildLoanView = (prestamo, pagosPrestamo = []) => {
  const schedule = Array.isArray(prestamo.tablaAmortizacion)
    ? prestamo.tablaAmortizacion
    : buildAmortizationSchedule({
        principal: prestamo.monto,
        annualRate: prestamo.tasaAnual,
        termMonths: prestamo.plazoMeses,
        startDate: prestamo.fechaInicio,
        paymentType: prestamo.tipoPago || "fijo",
      });

  const applied = applyPaymentsToSchedule(schedule, pagosPrestamo, prestamo.monto);

  return {
    ...prestamo,
    pagosPrestamo,
    scheduleApplied: applied.scheduleApplied,
    paymentsApplied: applied.paymentsApplied,
    resumen: applied.totals,
    pagoMensual: schedule[0]?.pagoProgramado || 0,
  };
};
